import { useContextBridge } from "@react-three/drei";
import { QueryClientProvider, useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";
import {
  UNSAFE_LocationContext,
  UNSAFE_NavigationContext,
} from "react-router-dom";

import { AuthContext } from "../features/auth/auth.context.js";
import { DesktopBridgeContext } from "../features/desktop-bridge/desktop-bridge.context.js";
import { FeatureFlagsContext } from "../features/feature-flags/feature-flags.context.js";
import { FluidContext } from "../features/fluid/fluid.provider.js";
import { OfficeContext } from "../features/office/office-context.js";
import { PlatformContext } from "../features/platform-detection/platform.context.js";
import { PresenceContext } from "../features/presence/presence.context.js";
import { SubscriptionContext } from "../features/subscription/subscription.context.js";
import { TableCallContext } from "../features/table-call/table-call.context.js";

/**
 * Both Html and Canvas components are rendered using new react DOM. That means the context is not transferred between React App -> Canvas and then from Canvas -> Html.
 * This hook simplifies the process of passing the context from one layer to another.
 * When new provider is introduced, just add it here and it should be good to go.
 */
export const useBezonedContext = () => {
  const queryClient = useQueryClient();

  const ContextBridgeInternal = useContextBridge(
    PlatformContext,
    DesktopBridgeContext,
    AuthContext,
    UNSAFE_LocationContext,
    UNSAFE_NavigationContext,
    SubscriptionContext,
    FeatureFlagsContext,
    OfficeContext,
    FluidContext,
    PresenceContext,
    TableCallContext
  );

  const ContextBridge = useMemo(
    () =>
      ({ children }: { children: React.ReactNode }) => (
        <QueryClientProvider client={queryClient}>
          <ContextBridgeInternal>{children}</ContextBridgeInternal>
        </QueryClientProvider>
      ),
    [queryClient, ContextBridgeInternal]
  );

  return { ContextBridge };
};
